import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import { useMutationGetCompanyMonitorCheck } from 'services/monitor/queries';
const SearchClientContext = createContext();

const useSearchClientContext = () => useContext(SearchClientContext);

const COMPANY_ADDED_ERROR = 'Company already added';
const COMPANY_EXIST_ERROR =
  "The company you've selected already exists in your portfolio. Try another company or unarchive clients.";
const COMPANY_CONNECTED_TO_ANOTHER_FIRM_ERROR =
  "The company you're selected is connected to another firm. Please contact us if you'd like to take ownership of this client.";

const SearchClientProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [existingCompanies, setExistingCompanies] = useState([]);
  const [archivedCompanies, setArchivedCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(null);
  const [maxMonitorClients, setMaxMonitorClients] = useState(null);
  const {
    mutate: getCompanyToMonitorCheck,
    isError: companyMonitorCheckError,
    isLoading: companyMonitorCheckLoading,
  } = useMutationGetCompanyMonitorCheck();

  useEffect(() => {
    if (companyMonitorCheckError) {
      setIsLoading(false);
      setIsError('Something went wrong. Please try again later.');
    }
    if (companyMonitorCheckLoading) {
      setIsError(null);
      setIsLoading(true);
    }
  }, [companyMonitorCheckError, companyMonitorCheckLoading]);

  const onCompanyRemove = useCallback(
    (removedCompany) => {
      Analytics.track(AnalyticsEventCategory.monitor, AnalyticsEventAction.addClientRemove);
      setSelectedCompanies((prevState) =>
        prevState.filter(
          (company) => company.registrationNumber !== removedCompany.registrationNumber,
        ),
      );
    },
    [setSelectedCompanies],
  );

  const onCompanyArchive = (archivedCompany) => {
    setExistingCompanies((prevState) =>
      prevState.filter((company) => company.entityId !== archivedCompany.entityId),
    );
    setArchivedCompanies((prevState) => [...prevState, archivedCompany]);
  };

  const checkCompanyAvailability = (data = {}) => {
    const { allowed, taken } = data;
    if (!taken) {
      return true;
    }
    setIsError(allowed ? COMPANY_EXIST_ERROR : COMPANY_CONNECTED_TO_ANOTHER_FIRM_ERROR);
    return false;
  };

  const checkIfCompanyIsSelected = (arr, selectedItem) =>
    arr.find((item) => item.registrationNumber === selectedItem.registrationNumber);

  const addCompanyToSelected = (selectedCompany) => {
    setSelectedCompanies((prevState) => {
      // this is here because of the issue with useState and closure
      const isCompanyAlreadySelected = checkIfCompanyIsSelected(prevState, selectedCompany);
      if (isCompanyAlreadySelected) {
        setIsError(COMPANY_ADDED_ERROR);
        return prevState;
      }
      Analytics.track(AnalyticsEventCategory.monitor, AnalyticsEventAction.addClientSelect);
      setIsOpen(false);
      return [...prevState, selectedCompany];
    });
  };

  const handleOnSelect = (entityId, { company: selectedCompany }) => {
    setIsLoading(true);
    setIsError(null);

    getCompanyToMonitorCheck(
      { entityId, companyNumber: selectedCompany?.registrationNumber },
      {
        onSuccess: (data) => {
          const canBeSelected = checkCompanyAvailability(data);
          if (canBeSelected) {
            addCompanyToSelected(selectedCompany);
          }
        },
        onError: (e) => {
          setIsError(e.message);
          return false;
        },
        onSettled: () => setIsLoading(false),
      },
    );
  };

  const handleOnChange = () => {
    setIsLoading(true);
    setIsError(false);
    setIsOpen(true);
  };

  const contextValue = {
    addedClientsCount: selectedCompanies.length,
    archivedCompanies,
    existingCompanies,
    handleOnChange,
    handleOnSelect,
    isError,
    isLoading,
    isOpen,
    maxMonitorClients,
    onCompanyArchive,
    onCompanyRemove,
    selectedCompanies,
    setExistingCompanies,
    setIsError,
    setIsLoading,
    setIsOpen,
    setMaxMonitorClients,
    setSelectedCompanies,
  };

  return (
    <SearchClientContext.Provider value={contextValue}>{children}</SearchClientContext.Provider>
  );
};

SearchClientProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { SearchClientProvider, useSearchClientContext };
